import { Box, Typography } from "@mui/material";

import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { type SystemChipItem, SystemChips } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import { DateRangeSelector } from "../../../components/UIToolkit/molecules/DateRangeSelector";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { INK } from "../../../theme/tokens";
import { ManagementCenterMultiSettings } from "../ManagementCenterMultiSettings";
import type { ManagementCenterChartKey } from "../utlis";

const getTimeChipsItems = ({
	isDemo,
	diffBetweenEarliestShiftAndNowInDays,
}: {
	diffBetweenEarliestShiftAndNowInDays: number;
	isDemo: boolean;
}): SystemChipItem<number>[] => [
	{
		label: "Last Quarter",
		value: 120,
		disabled: !isDemo && diffBetweenEarliestShiftAndNowInDays < 30,
	},
	{
		label: "Last Month",
		value: 30,
	},
	{
		label: "Last 2 Weeks",
		value: 14,
	},
];

type Props = {
	visibleCharts: ManagementCenterChartKey[];
	onVisibleChartsChange: (visible: ManagementCenterChartKey[]) => void;
	availableRegions: string[];
	selectedRegions: string[];
	onSelectedRegionsChange: (regions: string[]) => void;
	onTimePeriodChange: (days: number) => void;
	onStartDateChange: (date: DateTime) => void;
	onEndDateChange: (date: DateTime) => void;
};

export const ManagementCenterMultiHeader: FC<Props> = ({
	visibleCharts,
	onVisibleChartsChange,
	availableRegions,
	selectedRegions,
	onSelectedRegionsChange,
	onTimePeriodChange,
	onStartDateChange,
	onEndDateChange,
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isAnyDemo, timezone } = useDepartmentFlags();
	// The first day of the history the department loads: the Sunday its history-weeks setting reaches back to, the
	// same week the server starts the department's data from.
	const earliestShiftDate = useMemo(() => {
		const historyStart = DateTime.now().setZone(timezone).minus({ weeks: chosenDepartment.departmentDataHistoryWeeks }).startOf("day");
		return historyStart.minus({ days: historyStart.weekdayISO });
	}, [chosenDepartment.departmentDataHistoryWeeks, timezone]);
	const diffBetweenEarliestShiftAndNowInDays = useMemo(() => DateTime.now().diff(earliestShiftDate, "days").days, [earliestShiftDate]);
	const timeChipsItems = getTimeChipsItems({ diffBetweenEarliestShiftAndNowInDays, isDemo: isAnyDemo });
	const [selectedTimeChip, setSelectedTimeChip] = useState(timeChipsItems[1]);
	const currentDate = useMemo(() => DateTime.now(), []);
	const [customStartDate, setCustomStartDate] = useState<DateTime>(currentDate.minus({ days: 30 }));
	const [customEndDate, setCustomEndDate] = useState<DateTime>(currentDate);
	const [isCustomRangeEnabled, setIsCustomRangeEnabled] = useState(false);
	const handleCustomRangeEnabledChange = (enabled: boolean) => {
		setIsCustomRangeEnabled(enabled);
		if (enabled) {
			setSelectedTimeChip({ label: "", value: 0, disabled: false }); // Clear chip selection
		} else {
			setSelectedTimeChip(timeChipsItems[1]); // Reset to default chip
		}
	};

	const selectedDateFrame = useMemo(() => currentDate.minus({ days: selectedTimeChip.value }), [currentDate, selectedTimeChip]);

	useEffect(() => {
		if (isCustomRangeEnabled) {
			onStartDateChange(customStartDate);
			onEndDateChange(customEndDate);
		} else {
			onStartDateChange(selectedDateFrame);
			onEndDateChange(currentDate);
		}
	}, [selectedDateFrame, customStartDate, customEndDate, isCustomRangeEnabled, onStartDateChange, onEndDateChange, currentDate]);

	const changeTimeChip = (value: SystemChipItem<number>) => {
		setSelectedTimeChip(value);
		setIsCustomRangeEnabled(false);
		onTimePeriodChange(value.value);
	};

	return (
		<CommonDashboardBox
			sx={{
				width: "100%",
				height: "fit-content",
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: 2,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 2,
				}}
			>
				<Typography
					sx={{
						fontWeight: 700,
						fontSize: "24px",
						lineHeight: "100%",
						color: INK.SECONDARY,
						marginRight: 2,
					}}
				>
					Demo Health Network
				</Typography>

				<SystemChips
					variant={"filled-no-border"}
					forceFitContent
					items={timeChipsItems}
					onChange={changeTimeChip}
					selectedItems={isCustomRangeEnabled ? [] : [selectedTimeChip]}
				/>
				<DateRangeSelector
					disabled={!isCustomRangeEnabled}
					includeLeadingToggle={true}
					onEnabledChange={handleCustomRangeEnabledChange}
					startDate={customStartDate}
					endDate={customEndDate}
					onStartDateChange={setCustomStartDate}
					onEndDateChange={setCustomEndDate}
					minDate={earliestShiftDate}
					maxDate={currentDate}
				/>
				{availableRegions.length > 0 && (
					<SystemDropdown
						multiple
						label="All regions"
						items={availableRegions}
						selectedItems={selectedRegions}
						onChangeMultiple={onSelectedRegionsChange}
						// Keep search a pure filter: the old MUI Select never auto-picked a region, so don't let a
						// unique query match commit a selection (live mode) before the user explicitly chooses one.
						autoSelectSingleMatch={false}
						renderValue={(value) => {
							const values = Array.isArray(value) ? value : value ? [value] : [];
							return values.length === 0 ? "All regions" : values.join(", ");
						}}
						formControlSx={{ minWidth: 160 }}
					/>
				)}
			</Box>
			<ManagementCenterMultiSettings visibleCharts={visibleCharts} onVisibleChartsChange={onVisibleChartsChange} />
		</CommonDashboardBox>
	);
};
